import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, 
  TextField, Typography, Paper, CircularProgress, Grid, Checkbox, FormControlLabel,
  Alert, IconButton, alpha, useTheme, useMediaQuery
} from '@mui/material';
import { useSnackbar } from 'notistack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import LOAPDFViewer from './LOAPDFViewer';
import api from '../../utils/api'; // Import the configured API client

const LOAPDFWrapper: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const signatureSectionRef = useRef<HTMLDivElement>(null);
  
  // PDF and token state
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<any>(null);
  
  // LOA decision state
  const [acceptSelected, setAcceptSelected] = useState<boolean>(false);
  const [declineSelected, setDeclineSelected] = useState<boolean>(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState<boolean>(false);
  const [declineReason, setDeclineReason] = useState<string>('');
  const [accepted, setAccepted] = useState<boolean>(false);
  const [declined, setDeclined] = useState<boolean>(false);
  
  // Signature details
  const [name, setName] = useState<string>('');
  const [position, setPosition] = useState<string>(''); // No default - must come from DB
  const [signatureConfirmed, setSignatureConfirmed] = useState<boolean>(false);

  // Section styling with responsive padding - matching LOAPage design
  const sectionStyle = {
    background: `linear-gradient(135deg, ${alpha('#1976d2', 0.02)} 0%, ${alpha('#42a5f5', 0.04)} 100%)`,
    borderRadius: '16px',
    padding: { xs: theme.spacing(3), sm: theme.spacing(4) },
    border: `1px solid ${alpha('#1976d2', 0.12)}`,
    position: 'relative',
    overflow: 'hidden',
    color: '#1a1a1a',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s ease',
    '& .MuiTypography-root': {
      color: '#1a1a1a'
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '5px',
      height: '100%',
      background: 'linear-gradient(180deg, #1976d2 0%, #42a5f5 100%)',
      borderRadius: '16px 0 0 16px'
    }
  };

  useEffect(() => {
    // Extract the token from query parameters
    const queryParams = new URLSearchParams(location.search);
    const tokenParam = queryParams.get('token');
    
    console.log('%c LOAPDFWrapper initialized:', 'background: #222; color: #3498db', {
      token: tokenParam,
      pathname: location.pathname
    });
    
    if (tokenParam) {
      setToken(tokenParam);
      
      // Fetch client data and fresh presigned URL using the token
      const fetchData = async () => {
        try {
          setLoading(true);
          
          // Fetch client data
          const response = await api.get(`/loa/public/view/${tokenParam}`);
          console.log('Client data response:', response.data);
          
          if (response.data.success && response.data.client) {
            const clientData = response.data.client;
            setClient(clientData);
            setName(clientData.ownerName || '');
            setPosition(clientData.position || '');
            
            // Fetch fresh presigned URL
            if (clientData._id) {
              const pdfResponse = await api.get(`/loa/public/get-presigned-url/${clientData._id}`);
              console.log('Fresh PDF URL response:', pdfResponse.data);
              
              if (pdfResponse.data.success && pdfResponse.data.url) {
                console.log('%c Setting fresh PDF URL', 'background: #222; color: #2ecc71');
                console.log('%c Full URL:', 'background: #222; color: #3498db', pdfResponse.data.url);
                setPdfUrl(pdfResponse.data.url);
              } else {
                console.error('No URL in response:', pdfResponse.data);
              }
            }
          }
          
          setLoading(false);
        } catch (error) {
          console.error('Error fetching LOA data:', error);
          setError('Failed to load LOA document');
          setLoading(false);
        }
      };
      
      fetchData();
    }
    
    // Legacy support: Check if URL is passed in query string (old behavior)
    const url = queryParams.get('url');
    if (url && !tokenParam) {
      const decodedUrl = decodeURIComponent(url);
      
      // Check if URL has AWS signature (presigned URL)
      if (decodedUrl.includes('X-Amz-Signature')) {
        try {
          const urlObj = new URL(decodedUrl);
          const dateParam = urlObj.searchParams.get('X-Amz-Date');
          const expiresParam = urlObj.searchParams.get('X-Amz-Expires');
          
          if (dateParam && expiresParam) {
            // Parse AWS date format
            const year = parseInt(dateParam.substring(0, 4));
            const month = parseInt(dateParam.substring(4, 6)) - 1;
            const day = parseInt(dateParam.substring(6, 8));
            const hour = parseInt(dateParam.substring(9, 11));
            const minute = parseInt(dateParam.substring(11, 13));
            const second = parseInt(dateParam.substring(13, 15));
            
            const signedDate = new Date(Date.UTC(year, month, day, hour, minute, second));
            const expiresInSeconds = parseInt(expiresParam);
            const expirationDate = new Date(signedDate.getTime() + expiresInSeconds * 1000);
            const now = new Date();
            
            // Add 10-minute buffer - if URL expires within 10 minutes, treat as expired
            // This ensures we always have a fresh URL with plenty of time left
            const tenMinutesFromNow = new Date(now.getTime() + 10 * 60 * 1000);
            
            console.log('%c URL Expiration Check:', 'background: #222; color: #3498db', {
              expirationDate: expirationDate.toISOString(),
              currentTime: now.toISOString(),
              tenMinutesFromNow: tenMinutesFromNow.toISOString(),
              minutesUntilExpiry: Math.round((expirationDate.getTime() - now.getTime()) / 60000),
              willExpireSoon: expirationDate <= tenMinutesFromNow
            });
            
            // Check if URL is expired or expires within 10 minutes
            if (expirationDate <= tenMinutesFromNow) {
              console.log('%c URL from query string expires within 10 minutes, will fetch fresh URL', 'background: #222; color: #ff6b6b');
              // Don't set the expired URL, let the refresh mechanism handle it
            } else {
              console.log('%c Setting PDF URL from query string (valid for ' + Math.round((expirationDate.getTime() - now.getTime()) / 60000) + ' more minutes)', 'background: #222; color: #2ecc71');
              setPdfUrl(decodedUrl);
            }
          } else {
            // Can't determine expiration, set it anyway
            console.log('%c Setting PDF URL (cannot determine expiration):', 'background: #222; color: #f39c12', decodedUrl);
            setPdfUrl(decodedUrl);
          }
        } catch (parseError) {
          console.log('%c Error parsing URL, setting anyway:', 'background: #222; color: #f39c12', parseError);
          setPdfUrl(decodedUrl);
        }
      } else {
        // Not a presigned URL, set it directly
        console.log('%c Setting non-presigned PDF URL:', 'background: #222; color: #2ecc71', decodedUrl);
        setPdfUrl(decodedUrl);
      }
    } else if (!tokenParam) {
      console.log('%c No URL or token found in query params', 'background: #222; color: #ff6b6b');
      setError('No LOA document or token found. Please check the link and try again.');
    }
  }, [location]);

  // Refresh presigned URL every 45 minutes (before the 1-hour expiration)
  useEffect(() => {
    if (!client || !client._id) return;

    const refreshPresignedUrl = async () => {
      try {
        console.log('%c Refreshing presigned URL...', 'background: #222; color: #3498db');
        const response = await api.get(`/loa/public/get-presigned-url/${client._id}`);
        if (response.data.success && response.data.url) {
          console.log('%c Presigned URL refreshed successfully', 'background: #222; color: #2ecc71');
          setPdfUrl(response.data.url);
        }
      } catch (error) {
        console.error('Failed to refresh presigned URL:', error);
      }
    };

    // Refresh immediately if we don't have a URL
    if (!pdfUrl) {
      refreshPresignedUrl();
    }

    // Set up interval to refresh every 45 minutes
    const intervalId = setInterval(refreshPresignedUrl, 45 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [client, pdfUrl]);

  useEffect(() => {
    console.log('%c POSITION STATE CHANGED:', 'background: #222; color: #3498db', position);
  }, [position]);

  const handleSubmit = async () => {
    console.log('%c SUBMIT HANDLER - CURRENT STATE:', 'background: #222; color: #f39c12', {
      token,
      name,
      position,
      signatureConfirmed,
      acceptSelected,
      declineSelected
    });
    
    if (!token) {
      setError('Invalid LOA token');
      return;
    }

    if (acceptSelected) {
      console.log('%c ACCEPTANCE VALIDATION:', 'background: #222; color: #f39c12');
      
      if (!name) {
        console.log('Name validation failed');
        setError('Please provide your name');
        return;
      }

      if (!position) {
        console.log('%c Position validation failed - position is empty or null', 'background: #222; color: #ff6b6b');
        setError('Position is required. Please contact support if your position is not shown.');
        return;
      } else {
        console.log('%c Position validation passed:', 'background: #222; color: #2ecc71', position);
      }

      if (!signatureConfirmed) {
        console.log('Signature confirmation validation failed');
        setError('Please confirm you are authorized to sign this document');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('%c SENDING API REQUEST:', 'background: #222; color: #e74c3c', {
          endpoint: `/loa/public/accept/${token}`,
          payload: {
            name,
            position,
            signatureConfirmed
          }
        });

        // Ensure all values are properly formatted for the API
        const payload = {
          name: name.trim(),
          position: position.trim(),
          signatureConfirmed: signatureConfirmed === true // Ensure it's a boolean
        };
        
        console.log('%c FINAL API PAYLOAD:', 'background: #222; color: #e74c3c', payload);
        
        const response = await api.post(`/loa/public/accept/${token}`, payload);
        console.log('%c API RESPONSE:', 'background: #222; color: #2ecc71', response.data);
        
        setAccepted(true);
        enqueueSnackbar('Letter of Authority accepted successfully', { variant: 'success' });
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to accept LOA');
        enqueueSnackbar(error.response?.data?.message || 'Failed to accept LOA', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    } else if (declineSelected) {
      if (!declineReason.trim()) {
        setError('Please provide a reason for declining');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        await api.post(`/loa/public/decline/${token}`, { reason: declineReason });
        setDeclined(true);
        
        enqueueSnackbar('Letter of Authority declined', { variant: 'info' });
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to decline LOA');
        enqueueSnackbar(error.response?.data?.message || 'Failed to decline LOA', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDecline = async () => {
    if (!token) {
      setError('Invalid LOA token');
      return;
    }

    if (!declineReason.trim()) {
      setError('Please provide a reason for declining');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await api.post(`/loa/public/decline/${token}`, { reason: declineReason });
      setDeclined(true);
      setShowDeclineDialog(false);
      
      enqueueSnackbar('Letter of Authority declined', { variant: 'info' });
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to decline LOA');
      enqueueSnackbar(error.response?.data?.message || 'Failed to decline LOA', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!pdfUrl) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>;
  }

  // If the LOA has been accepted or declined, show a confirmation screen
  if (accepted || declined) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: { xs: 2, sm: 3 },
        textAlign: 'center'
      }}>
        {accepted ? (
          <>
            <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom>Thank You!</Typography>
            <Typography variant="body1" paragraph>
              You have successfully accepted the Letter of Authority. Our team will process your request shortly.
            </Typography>
          </>
        ) : (
          <>
            <CancelIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom>LOA Declined</Typography>
            <Typography variant="body1" paragraph>
              You have declined the Letter of Authority. If you have any questions, please contact our support team.
            </Typography>
          </>
        )}
        <Button 
          variant="contained" 
          onClick={() => window.close()}
          sx={{ mt: 2 }}
        >
          Close Window
        </Button>
      </Box>
    );
  }

  // If the LOA has been accepted or declined, show the confirmation screen
  if (accepted) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', p: 3, alignItems: 'center', justifyContent: 'center' }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h4" gutterBottom>Thank You</Typography>
        <Typography variant="body1" align="center" paragraph>
          Your Letter of Authority has been accepted successfully.
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center">
          You may now close this window.
        </Typography>
      </Box>
    );
  }

  if (declined) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', p: 3, alignItems: 'center', justifyContent: 'center' }}>
        <CancelIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h4" gutterBottom>Letter of Authority Declined</Typography>
        <Typography variant="body1" align="center" paragraph>
          Your decision has been recorded.
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center">
          You may now close this window.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)'
    }}>
      {/* Header with title */}
      <Paper elevation={3} sx={{ 
        p: { xs: 2, sm: 3 }, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: '#ffffff',
        borderRadius: 0,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.5px'
          }}
        >
          Letter of Authority
        </Typography>
      </Paper>
      
      {/* Error message if any */}
      {error && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 1, 
            mt: 1, 
            mb: 1, 
            mx: 2, 
            bgcolor: 'error.light', 
            color: 'error.contrastText',
            borderRadius: 1
          }}
        >
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}
      
      {/* Main content area */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexGrow: 1 }}>
        {/* PDF Viewer */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isMobile && (
            <Alert severity="info" sx={{ m: 2, mb: 1 }}>
              <Typography variant="body2">
                <strong>Mobile Tip:</strong> If the document doesn't display properly, scroll down to use the "Download PDF" button in the decision panel.
              </Typography>
            </Alert>
          )}
          {pdfUrl ? (
            <LOAPDFViewer pdfUrl={pdfUrl} />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              minHeight: '400px'
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body1" color="textSecondary">
                  Loading document...
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        
        {/* Decision Panel */}
        <Box sx={{ 
          width: { xs: '100%', md: '400px' },
          p: { xs: 2, sm: 3 }, 
          overflowY: 'auto',
          borderLeft: { xs: 'none', md: `1px solid ${alpha(theme.palette.divider, 0.1)}` },
          borderTop: { xs: `1px solid ${alpha(theme.palette.divider, 0.1)}`, md: 'none' }
        }}>
          <Paper elevation={3} sx={{ 
            p: { xs: 2, sm: 3 },
            borderRadius: '20px',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            {/* Document viewed confirmation */}
            <Box mb={{ xs: 2, sm: 4 }}>
              <Alert severity="success" sx={{ mb: { xs: 1.5, sm: 2 }, py: { xs: 0.5, sm: 1 } }}>
                Document viewed successfully
              </Alert>
              
              {/* Download button for mobile users */}
              {isMobile && pdfUrl && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => window.open(pdfUrl, '_blank')}
                    fullWidth
                    size="large"
                  >
                    Download PDF
                  </Button>
                </Box>
              )}
              
              {/* Decision Section */}
              <Box mb={{ xs: 2, sm: 4 }} sx={sectionStyle}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    fontWeight: 600,
                    color: '#1976d2',
                    mb: 2
                  }}
                >
                  Your Decision
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={{ xs: 1, sm: 2 }}>
                  Please select whether you wish to accept or decline this Letter of Authority:
                </Typography>
                {/* Only show Accept/Decline buttons if no option has been selected yet */}
                {!acceptSelected && !declineSelected ? (
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    <Grid item xs={12} sm="auto">
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          setAcceptSelected(true);
                          setDeclineSelected(false);
                          // Scroll to signature section after a short delay
                          setTimeout(() => {
                            signatureSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 100);
                        }}
                        size="large"
                        fullWidth={true}
                        sx={{ mb: { xs: 1, sm: 0 } }}
                      >
                        Accept
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setDeclineSelected(true);
                          setAcceptSelected(false);
                        }}
                        size="large"
                        fullWidth={true}
                      >
                        Decline
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body1" color="primary" fontWeight="bold">
                    You have chosen to {acceptSelected ? "accept" : "decline"} the Letter of Authority.
                  </Typography>
                )}
              </Box>
              
              {/* Signature Section - Only shown if Accept is selected */}
              {acceptSelected && (
                <Box mb={{ xs: 2, sm: 4 }} sx={sectionStyle} ref={signatureSectionRef}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      fontWeight: 600,
                      color: '#1976d2',
                      mb: 2
                    }}
                  >
                    Signature Details
                  </Typography>
                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Your Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={!name && signatureConfirmed}
                        helperText={!name && signatureConfirmed ? 'Name is required' : ''}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Position"
                        value={position}
                        InputProps={{
                          readOnly: true,
                        }}
                        variant="outlined"
                        sx={{ width: '100%' }}
                        helperText={position ? "Position from company records" : "No position found in records"}
                        error={!position}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={signatureConfirmed}
                            onChange={(e) => setSignatureConfirmed(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="I confirm that I am authorised to sign this Letter of Authority on behalf of the company"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Decline Reason Section - Only shown if Decline is selected */}
              {declineSelected && (
                <Box mb={{ xs: 2, sm: 4 }} sx={sectionStyle}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      fontWeight: 600,
                      color: '#1976d2',
                      mb: 2
                    }}
                  >
                    Reason for Declining
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mb={{ xs: 1, sm: 2 }}>
                    Please provide a reason for declining this Letter of Authority:
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    label="Reason"
                    multiline
                    rows={4}
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    error={declineSelected && !declineReason}
                    helperText={declineSelected && !declineReason ? 'A reason is required when declining' : ''}
                  />
                </Box>
              )}
              
              {/* Submit Button - Only shown if a decision has been made */}
              {(acceptSelected || declineSelected) && (
                <Box mt={{ xs: 2, sm: 3 }} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 1, sm: 2 }}>
                  <Button
                    variant="contained"
                    color={declineSelected ? "error" : "primary"}
                    onClick={handleSubmit}
                    disabled={loading || 
                      (acceptSelected && (!signatureConfirmed || !name || !position)) || 
                      (declineSelected && !declineReason)}
                    size="large"
                    fullWidth={true}
                    sx={{ mb: { xs: 1, sm: 0 } }}
                  >
                    {loading ? <CircularProgress size={24} /> : `Submit ${acceptSelected ? 'Acceptance' : 'Decline'}`}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setAcceptSelected(false);
                      setDeclineSelected(false);
                      setDeclineReason('');
                    }}
                    disabled={loading}
                    fullWidth={true}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
      
      {/* Decline Dialog - Only used for quick decline from button */}
      <Dialog 
        open={showDeclineDialog} 
        onClose={() => setShowDeclineDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { margin: { xs: '16px', sm: '32px' } } }}
      >
        <DialogTitle>
          Decline Letter of Authority
          <IconButton
            aria-label="close"
            onClick={() => setShowDeclineDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for declining"
            fullWidth
            multiline
            rows={4}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeclineDialog(false)}>Cancel</Button>
          <Button onClick={handleDecline} color="error">Decline</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LOAPDFWrapper;
